import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  FormControlLabel,
  Switch,
  Badge,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import ChatTab from "./components/ChatTab";
import request from "../../utilities/requests";
import { getSocket } from "../../utilities/socketService";
import { toast } from "../../utilities/SnackBarUtils";

// ── My Team Tab ────────────────────────────────────────────────────
const MyTeamTab = ({ myTeam }) => {
  if (!myTeam) {
    return (
      <Box sx={{ px: 2, pt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          You haven't been assigned to a team yet.
        </Typography>
      </Box>
    );
  }

  const captainIds = new Set(
    (myTeam.captains || []).map((c) => (c?._id || c)?.toString())
  );
  const isCaptain = (player) => captainIds.has((player?._id || player)?.toString());
  const sortedRoster = [...(myTeam.roster || [])].sort(
    (a, b) => (isCaptain(b) ? 1 : 0) - (isCaptain(a) ? 1 : 0)
  );

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Card variant="outlined">
        <CardContent sx={{ pb: "12px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            {myTeam.team?.color && (
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: myTeam.team.color,
                  flexShrink: 0,
                }}
              />
            )}
            <Typography variant="h6">{myTeam.team?.name}</Typography>
            {myTeam.division && (
              <Chip label={myTeam.division} size="small" variant="outlined" />
            )}
          </Box>
          {sortedRoster.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Roster
              </Typography>
              <List dense disablePadding>
                {sortedRoster.map((player) => {
                  const firstName = player?.profile?.firstName || "";
                  const lastName = player?.profile?.lastName || "";
                  const name = `${firstName} ${lastName}`.trim();
                  if (!name) return null;
                  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
                  const captain = isCaptain(player);
                  return (
                    <ListItem key={player._id} disableGutters>
                      <ListItemAvatar>
                        <Avatar
                          src={player.profile?.imageUrl || undefined}
                          sx={{ width: 36, height: 36 }}
                        >
                          {initials}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            {name}
                            {captain && (
                              <Chip label="Captain" size="small" color="warning" variant="outlined" />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// ── Schedule Tab ───────────────────────────────────────────────────
const ScheduleTab = ({ games, myTeamId, themedWeeks, milestones }) => {
  const [showAll, setShowAll] = useState(false);

  const formatDate = (dt) => dayjs(dt).format("MMM D");
  const formatTime = (dt) => dayjs(dt).format("h:mm A");

  const themeForGame = (game) => {
    const t = dayjs(game.scheduledAt);
    return (themedWeeks || []).find((w) => dayjs(w.date).isSame(t, "day"));
  };

  const ThemeChip = ({ game }) => {
    const theme = themeForGame(game);
    if (!theme) return null;
    return (
      <Chip
        label={theme.label}
        size="small"
        variant="outlined"
        sx={{ borderColor: theme.color || undefined, color: theme.color || undefined }}
      />
    );
  };

  const winnerOf = (game, side) => {
    if (!game.played) return false;
    if (game.team1Score === game.team2Score) return false;
    return side === 1
      ? game.team1Score > game.team2Score
      : game.team2Score > game.team1Score;
  };

  const isMyGame = (g) =>
    myTeamId &&
    (g.team1?._id?.toString() === myTeamId || g.team2?._id?.toString() === myTeamId);

  const showOnlyMine = myTeamId && !showAll;
  const visibleGames = showOnlyMine ? games.filter(isMyGame) : games;

  const upcomingGames = visibleGames
    .filter((g) => !g.played)
    .sort((a, b) => dayjs(a.scheduledAt).valueOf() - dayjs(b.scheduledAt).valueOf());
  const completedGames = visibleGames
    .filter((g) => g.played)
    .sort((a, b) => dayjs(b.scheduledAt).valueOf() - dayjs(a.scheduledAt).valueOf());

  const upcomingItems = [
    ...upcomingGames.map((g) => ({ type: "game", date: g.scheduledAt, game: g })),
    ...(milestones || [])
      .filter((m) => !dayjs(m.date).isBefore(dayjs(), "day"))
      .map((m) => ({ type: "milestone", date: m.date, milestone: m })),
  ].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      {myTeamId && (
        <FormControlLabel
          control={
            <Switch
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
          }
          label="Show all league games"
          sx={{ mb: 1 }}
        />
      )}

      {/* Upcoming */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Upcoming
      </Typography>
      {upcomingItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No upcoming games scheduled.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {upcomingItems.map((item, idx) =>
            item.type === "milestone" ? (
              <Card key={`m-${idx}`} variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(item.milestone.date)}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {item.milestone.label}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card key={item.game._id} variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(item.game.scheduledAt)} · {formatTime(item.game.scheduledAt)}
                      {item.game.field ? ` · ${item.game.field}` : ""}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                      <ThemeChip game={item.game} />
                      {item.game.isPreseason && (
                        <Chip label="Pre-season" size="small" color="warning" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                  <Typography variant="body1">
                    {item.game.team1?.name || "TBD"} vs {item.game.team2?.name || "TBD"}
                  </Typography>
                </CardContent>
              </Card>
            ),
          )}
        </Box>
      )}

      {/* Completed */}
      {completedGames.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Results
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {completedGames.map((game) => (
              <Card key={game._id} variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(game.scheduledAt)}
                      {game.field ? ` · ${game.field}` : ""}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                      <ThemeChip game={game} />
                      {game.isPreseason && (
                        <Chip label="Pre-season" size="small" color="warning" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: winnerOf(game, 1) ? 700 : 400 }}>
                        {game.team1?.name || "TBD"}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: winnerOf(game, 2) ? 700 : 400 }}>
                        {game.team2?.name || "TBD"}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {game.team1Score}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {game.team2Score}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

// ── Standings Tab ──────────────────────────────────────────────────
const StandingsTab = ({ standings, myTeamId, publishedAt }) => {
  if (!standings || standings.length === 0) {
    return (
      <Box sx={{ px: 2, pt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Standings are not available yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      {standings.map((division) => (
        <Box key={division.division} sx={{ mb: 2 }}>
          {standings.length > 1 && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {division.division}
            </Typography>
          )}
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, pl: 1, pr: 0.5 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">W</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">L</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">T</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">+/-</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {division.teams.map((team) => (
                  <TableRow
                    key={team.team}
                    sx={
                      myTeamId && team.team?.toString() === myTeamId
                        ? { backgroundColor: "action.hover" }
                        : {}
                    }
                  >
                    <TableCell sx={{ pl: 1, pr: 0.5 }}>{team.position}</TableCell>
                    <TableCell>{team.teamName}</TableCell>
                    <TableCell align="center">{team.wins}</TableCell>
                    <TableCell align="center">{team.losses}</TableCell>
                    <TableCell align="center">{team.ties}</TableCell>
                    <TableCell align="center">
                      {team.pointDiff > 0 ? `+${team.pointDiff}` : team.pointDiff}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ))}
      {publishedAt && (
        <Typography variant="caption" color="text.secondary">
          Last updated {dayjs(publishedAt).format("MMM D, YYYY h:mm A")}
        </Typography>
      )}
    </Box>
  );
};

const buildTabs = (leagueEvent) => [
  { key: "team", label: "My Team" },
  { key: "schedule", label: "Schedule" },
  { key: "standings", label: "Standings" },
  ...(leagueEvent?.chatEnabled ? [{ key: "chat", label: "Chat" }] : []),
];

// ── Main Page ──────────────────────────────────────────────────────
const MyLeague = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [leagueEvent, setLeagueEvent] = useState(null);
  const [games, setGames] = useState([]);
  const [standings, setStandings] = useState(null);
  const [myTeam, setMyTeam] = useState(null);
  const [tab, setTab] = useState(1);
  const [unread, setUnread] = useState(0);

  const onChatTabRef = useRef(false);
  const viewerIdRef = useRef(null);

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/league", title: "MyLeague" });
  }, []);

  useEffect(() => {
    if (eventId) loadAll();
  }, [eventId]);

  useEffect(() => {
    const count = buildTabs(leagueEvent).length;
    if (tab <= count - 1) return;
    setTab(1);
    toast.info("The organizer has turned off league chat");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueEvent?.chatEnabled]);

  useEffect(() => {
    const leagueEventId = leagueEvent?._id;
    if (!leagueEventId) return undefined;

    let attached = null;
    let intervalId = null;

    const loadUnread = async () => {
      const result = await request(
        `leaguechat/unread?leagueEventId=${leagueEventId}`
      );
      if (result?.success) {
        if (result.viewerId) viewerIdRef.current = result.viewerId;
        setUnread(result.unread || 0);
      }
    };

    const bumpUnread = (message) => {
      if (onChatTabRef.current) return;
      if (
        viewerIdRef.current &&
        String(message?.authorId) === String(viewerIdRef.current)
      ) {
        return;
      }
      setUnread((prev) => prev + 1);
    };

    const onToggled = (payload) => {
      if (String(payload.leagueEventId) !== String(leagueEventId)) return;
      setLeagueEvent((prev) =>
        prev ? { ...prev, chatEnabled: payload.chatEnabled } : prev
      );
      if (!payload.chatEnabled) setUnread(0);
    };

    const onRevoked = (payload) => {
      if (String(payload.leagueEventId) !== String(leagueEventId)) return;
      setUnread(0);
      setLeagueEvent((prev) => (prev ? { ...prev, chatEnabled: false } : prev));
    };

    const onConnect = () => {
      attached?.emit("joinLeagueChat", leagueEventId);
      loadUnread();
    };

    const attach = (s) => {
      attached = s;
      s.emit("joinLeagueChat", leagueEventId);
      s.on("leagueChat:message", bumpUnread);
      s.on("leagueChat:toggled", onToggled);
      s.on("leagueChat:revoked", onRevoked);
      s.on("connect", onConnect);
    };

    const existing = getSocket();
    if (existing) {
      attach(existing);
    } else {
      intervalId = setInterval(() => {
        const s = getSocket();
        if (s) {
          attach(s);
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 1000);
    }

    loadUnread();

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (attached) {
        attached.emit("leaveLeagueChat", leagueEventId);
        attached.off("leagueChat:message", bumpUnread);
        attached.off("leagueChat:toggled", onToggled);
        attached.off("leagueChat:revoked", onRevoked);
        attached.off("connect", onConnect);
      }
    };
  }, [leagueEvent?._id]);

  const trackTabView = (index, leagueName) => {
    const tabs = buildTabs(leagueEvent);
    ReactGA.event({
      category: "league",
      action: "view_tab",
      label: `${leagueName || "Unknown"} - ${tabs[index]?.label || index}`,
    });
  };

  const handleTabChange = (_, v) => {
    setTab(v);
    trackTabView(v, leagueEvent?.event?.name);
  };

  const loadAll = async () => {
    setLoading(true);

    const leResult = await request(`leagueevent?eventId=${eventId}`);
    if (!leResult?.success) {
      setLoading(false);
      return;
    }
    const le = leResult.data;
    setLeagueEvent(le);
    trackTabView(1, le.event?.name);

    const [gResult, sResult, tResult] = await Promise.all([
      request(`leaguegame?eventId=${eventId}`),
      request(`leagueevent/standings?leagueEventId=${le._id}`),
      request(`leagueevent/myteam?leagueEventId=${le._id}`),
    ]);

    if (gResult?.success) setGames(gResult.data);
    if (sResult?.success) setStandings(sResult.data);
    if (tResult?.success) setMyTeam(tResult.data);

    setLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!leagueEvent) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">League not found.</Typography>
      </Box>
    );
  }

  const event = leagueEvent.event;
  const myTeamId = myTeam?.team?._id?.toString();
  const tabs = buildTabs(leagueEvent);
  const activeTab = Math.min(tab, tabs.length - 1);
  const activeKey = tabs[activeTab]?.key;
  onChatTabRef.current = activeKey === "chat";

  return (
    <Box sx={{ pb: 10, maxWidth: 600, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 2, pb: 1 }}>
        <IconButton onClick={() => navigate("/")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">{event?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {(() => {
              const parts = [];
              if (event?.start || event?.end) {
                const s = event.start ? dayjs(event.start).format("MMM D") : "";
                const e = event.end ? dayjs(event.end).format("MMM D, YYYY") : "";
                parts.push(s && e ? `${s} – ${e}` : s || e);
              }
              if (leagueEvent.sportType) parts.push(leagueEvent.sportType);
              if (event?.venueName) parts.push(event.venueName);
              return parts.join(" · ");
            })()}
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.key}
            label={
              t.key === "chat" && unread > 0 ? (
                <Badge
                  color="error"
                  badgeContent={unread > 99 ? "99+" : unread}
                  sx={{ "& .MuiBadge-badge": { right: -12, top: 2 } }}
                >
                  {t.label}
                </Badge>
              ) : (
                t.label
              )
            }
          />
        ))}
      </Tabs>

      {activeKey === "team" && <MyTeamTab myTeam={myTeam} />}
      {activeKey === "schedule" && (
        <ScheduleTab
          games={games}
          myTeamId={myTeamId}
          themedWeeks={leagueEvent.themedWeeks}
          milestones={leagueEvent.milestones}
        />
      )}
      {activeKey === "standings" && (
        <StandingsTab
          standings={standings}
          myTeamId={myTeamId}
          publishedAt={leagueEvent.standingsPublishedAt}
        />
      )}
      {activeKey === "chat" && (
        <ChatTab
          leagueEventId={leagueEvent._id}
          onRead={() => setUnread(0)}
        />
      )}
    </Box>
  );
};

export default MyLeague;
